import { Download, ExternalLink, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import { api } from "@/api";

const STATUS = { todo: "À traiter", standby: "En attente", won: "Gagnée", lost: "Perdue", cancelled: "Annulée" };
const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

export function LeadsDrawer({ spec, baseParams, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    const params = { ...baseParams, ...spec.params };
    api.get("/agent-stats/leads", { params }).then(({ data: d }) => setData(d)).catch(() => setError(true));
  }, [spec, baseParams]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = data?.leads || [];
    if (!needle) return list;
    return list.filter((l) => [l.title, l.created_by_name, l.pipeline, l.step, ...(l.tags || [])].some((v) => String(v || "").toLowerCase().includes(needle)));
  }, [data, q]);

  const exportCsv = () => {
    const head = ["Opportunité", "Agent", "Pipeline", "Étape", "Statut", "Créée le", "Tags", "Lien noCRM"];
    const lines = rows.map((l) => [l.title, l.created_by_name, l.pipeline, l.step, STATUS[l.status] || l.status, new Date(l.created_at).toLocaleDateString("fr-FR"), (l.tags || []).join(" | "), data?.nocrm_base_url ? `${data.nocrm_base_url}${l.lead_id}` : ""].map(csvCell).join(";"));
    const blob = new Blob([`\ufeff${[head.map(csvCell).join(";"), ...lines].join("\n")}`], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "opportunites.csv";
    a.click();
  };

  return createPortal(
    <div className="leads-drawer-backdrop" onClick={onClose} data-testid="leads-drawer-backdrop">
      <aside className="leads-drawer" onClick={(e) => e.stopPropagation()} data-testid="leads-drawer">
        <header className="leads-drawer-head">
          <div>
            <p className="eyebrow">OPPORTUNITÉS</p>
            <h3 data-testid="leads-drawer-title">{spec.title}</h3>
            <p className="leads-drawer-sub" data-testid="leads-drawer-count">{spec.period} · {data ? `${data.total.toLocaleString("fr-FR")} opportunité${data.total > 1 ? "s" : ""}` : "Chargement…"}</p>
          </div>
          <button type="button" className="agent-group-icon" onClick={onClose} title="Fermer" data-testid="leads-drawer-close"><X size={16} /></button>
        </header>
        <div className="leads-drawer-tools">
          <div className="agent-filter-search"><Search size={14} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (nom, agent, étape, tag…)" data-testid="leads-drawer-search" /></div>
          <button type="button" className="secondary-button agent-overview-btn" disabled={!rows.length} onClick={exportCsv} data-testid="leads-drawer-export"><Download size={14} />CSV</button>
        </div>
        {error && <p className="leads-drawer-empty">Impossible de charger la liste.</p>}
        {data?.truncated && <p className="leads-drawer-note">Affichage limité aux {data.leads.length.toLocaleString("fr-FR")} plus récentes.</p>}
        <div className="leads-drawer-list">
          {data && rows.length === 0 && <p className="leads-drawer-empty">Aucune opportunité.</p>}
          {rows.map((l) => (
            <article key={l.lead_id} className="leads-item" data-testid={`leads-item-${l.lead_id}`}>
              <div className="leads-item-top">
                <strong>{l.title || `Opportunité ${l.lead_id}`}</strong>
                {data?.nocrm_base_url && (
                  <a href={`${data.nocrm_base_url}${l.lead_id}`} target="_blank" rel="noreferrer" className="leads-item-link" data-testid={`leads-open-${l.lead_id}`}>Ouvrir dans noCRM<ExternalLink size={12} /></a>
                )}
              </div>
              <p className="leads-item-meta">{l.created_by_name} · {l.pipeline} · {l.step} · <span className={`leads-status leads-status-${l.status}`}>{STATUS[l.status] || l.status}</span> · {new Date(l.created_at).toLocaleDateString("fr-FR")}</p>
              {l.tags?.length > 0 && <p className="leads-item-tags">{l.tags.filter((t) => !/^[0-9a-f]{32}$/.test(t)).join(" · ")}</p>}
            </article>
          ))}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
